import { useMemo } from 'react';
import { useParams } from 'react-router';

import { createAssistFilterOptions } from '../chats/helpers';
import { INTENT_FILTER_PARAM_NAME } from '../constants';
import {
  dateRangeToTimestamp,
  isUiQueryError,
  normalizeFilters,
} from '../helpers';
import { SearchQueryMode } from '../types';
import { useNormalizeChannelsForBuildQueryFromMultiSelected } from './useNormalizeChannels';
import {
  listCVObjectDeserialize,
  listCVObjectSerialize,
  listEventObjectDeserialize,
  listEventObjectSerialize,
} from 'src/components/global-filter-drawer/helper';
import { ContextVariableFilterItemValue } from 'src/components/global-filter-drawer/types';
import { useIsInAssist, useStateParams } from 'src/hooks/hooks';
import { buildQueryFromMultiSelected } from 'src/pages/intent-conversation-analytics/hooks/useConversations';
import { EventChartProperty } from 'src/services/insight-cards/types';
import { ConversationChannel } from 'src/types/workflowBuilderAPITypes';
import {
  dateRangeDeserialize,
  dateRangeSerialize,
  genericSerializeAndDeserialize,
  listDeserialize,
  listSerialize,
  timeFilterParameterValidator,
} from 'src/utils/discover/helpers';
import { ExportableTableType } from 'src/utils/enums';
import { last30DaysTimeRange } from 'src/utils/timeRangeHelpers';

export const useChatsTableRequestParams = (channels: ConversationChannel[]) => {
  const { cardId } = useParams<'cardId'>();
  const { articleId = undefined } = useParams<'articleId'>();
  const { topicId = undefined } = useParams<'topicId'>();
  const { workflowId = undefined } = useParams<'workflowId'>();
  const isParentTopic = location.pathname.includes('parent');

  const [dateRange] = useStateParams({
    deserialize: dateRangeDeserialize,
    initialState: last30DaysTimeRange,
    paramsName: 'date',
    serialize: dateRangeSerialize,
    validator: timeFilterParameterValidator([]),
  });

  const backendReadyTimestamps = dateRangeToTimestamp(dateRange);

  const [cvFilters] = useStateParams<ContextVariableFilterItemValue[]>({
    deserialize: listCVObjectDeserialize,
    initialState: [],
    paramsName: `${INTENT_FILTER_PARAM_NAME}_cv`,
    serialize: listCVObjectSerialize,
  });
  const [eventFilters] = useStateParams<EventChartProperty[]>({
    deserialize: listEventObjectDeserialize,
    initialState: [],
    paramsName: `${INTENT_FILTER_PARAM_NAME}_event`,
    serialize: listEventObjectSerialize,
  });
  const [multiSelected] = useStateParams<string[]>({
    deserialize: listDeserialize,
    initialState: [],
    paramsName: INTENT_FILTER_PARAM_NAME,
    serialize: listSerialize,
  });
  const [searchQuery] = useStateParams<string>({
    deserialize: (str: string) => (str === 'null' ? '' : str),
    initialState: '',
    paramsName: 'chats_search',
    serialize: genericSerializeAndDeserialize,
  });
  const [searchQueryMode] = useStateParams<SearchQueryMode>({
    deserialize: genericSerializeAndDeserialize as (
      str: string,
    ) => SearchQueryMode,
    initialState: 'conversation_id',
    paramsName: 'chats_search_mode',
    serialize: genericSerializeAndDeserialize,
  });

  const uiQueryError = isUiQueryError({
    searchQueryMode,
    uiQuery: searchQuery,
  });

  const normalizeChannelsForBuildQueryFromMultiSelected =
    useNormalizeChannelsForBuildQueryFromMultiSelected();

  const [multiSelectedAssist] = useStateParams<string[]>({
    deserialize: listDeserialize,
    initialState: [],
    paramsName: `${INTENT_FILTER_PARAM_NAME}_assist`,
    serialize: listSerialize,
  });
  const assistFilterOptions = useMemo(
    () => createAssistFilterOptions(multiSelectedAssist),
    [multiSelectedAssist],
  );

  const chatsTableRequestParams = useMemo(
    () =>
      normalizeFilters({
        article_id: articleId,
        channels: buildQueryFromMultiSelected(
          normalizeChannelsForBuildQueryFromMultiSelected(channels),
        )?.channels,
        chart_id: cardId,
        chat: searchQueryMode === 'chat' ? searchQuery : '',
        conversation_id:
          searchQueryMode === 'conversation_id' && !uiQueryError
            ? searchQuery
            : '',
        cv_filters: cvFilters,
        data_export_type: ExportableTableType.INSIGHT_CHATS_TABLE,
        end: backendReadyTimestamps.end_timestamp,
        event_filters: eventFilters,
        is_parent_topic: isParentTopic,
        start: backendReadyTimestamps.start_timestamp,
        topic_id: topicId,
        user_query: searchQueryMode === 'user_query' ? searchQuery : '',
        widget_ticket_id:
          searchQueryMode === 'widget_ticket_id' ? searchQuery : '',
        workflow_id: workflowId,
        ...buildQueryFromMultiSelected(multiSelected),
      }),
    [
      articleId,
      normalizeChannelsForBuildQueryFromMultiSelected,
      channels,
      cardId,
      searchQueryMode,
      searchQuery,
      uiQueryError,
      cvFilters,
      backendReadyTimestamps.end_timestamp,
      backendReadyTimestamps.start_timestamp,
      eventFilters,
      isParentTopic,
      topicId,
      workflowId,
      multiSelected,
    ],
  );

  const isInAssist = useIsInAssist();

  const [searchText] = useStateParams<string>({
    deserialize: (value: string) => value,
    initialState: '',
    paramsName: 'source_id',
    serialize: (value: string) => value,
  });
  const [searchType] = useStateParams<string>({
    deserialize: (value: string) => value,
    initialState: 'source_id',
    paramsName: 'assist_search_type',
    serialize: (value: string) => value,
  });

  const assistTableRequestParams = useMemo(
    () => ({
      agent_email: searchType === 'agent_email' ? searchText : undefined,
      data_export_type: ExportableTableType.INSIGHT_ASSIST_CHATS_TABLE,
      end: backendReadyTimestamps.end_timestamp,
      source_id: searchType === 'source_id' ? searchText : undefined,
      start: backendReadyTimestamps.start_timestamp,
      ...assistFilterOptions,
    }),
    [
      assistFilterOptions,
      backendReadyTimestamps.end_timestamp,
      backendReadyTimestamps.start_timestamp,
      searchText,
      searchType,
    ],
  );

  return isInAssist
    ? ({
        assist_table_request: assistTableRequestParams,
      } as const)
    : ({ chats_table_request: chatsTableRequestParams } as const);
};
